import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Access, EmployeeSubtype, EmployeeType } from '@prisma/client';
import { Breadcrumb, Button, Form, Input, Layout, Radio, Result, Select, Space, Spin } from 'antd';
import Descriptions, { DescriptionsItemType } from 'antd/es/descriptions';
import { DefaultOptionType } from 'antd/es/select';

import { SourceAccessItem } from '@app/domain-access-interfaces';
import { Auth, Hooks, PageLayout, Picker, Tables } from '@app/web-components';

import { accessesMatchesEmployeeType } from '../../../../bundles/forms/rules/access-matches-employee-type';
import { AccessSelect } from '../../../../common/components';
import { NotFoundPage } from '../../../../common/pages/not-found';
import { useCreateAccessRule, useGetAccessRule } from '../../../rules/data/access-rule';
import { exceptionUniquenessValidator, FormException } from '../../../rules/libs';
import { useFindAccessById } from '../../data';
import { DepartmentCascader } from '../../forms/inputs/department-cascader';

interface AccessConflictFormData {
  employeeType?: EmployeeType;
  employeeSubtype?: EmployeeSubtype;
  access?: SourceAccessItem | Access;
  department?: string;
  owners: DefaultOptionType[];
  exceptions: FormException[];
}

export const RuleCreate = () => {
  const [form] = Form.useForm<AccessConflictFormData>();
  const employeeType = Form.useWatch('employeeType', form);
  const owners = Form.useWatch('owners', form);
  const formLayoutProps = Hooks.useFormLayout();

  const { id } = useParams();
  const accessId = Number(id) | 0;
  const { user } = Auth.useUserAuth();

  const [searchParams] = useSearchParams();
  const copyRuleId = Number(searchParams.get('id')) | 0;
  const ruleType = searchParams.get('ruleType');

  const { data, isFetching } = useFindAccessById(accessId, { enabled: !!accessId });
  const copyQuery = useGetAccessRule(copyRuleId, { enabled: !!copyRuleId });
  const create = useCreateAccessRule(accessId);
  const PAGE_TITLE = `Create ${ruleType === 'access-conflict' ? 'Access' : 'Department'} Conflict`;

  const { getSelectProps } = Picker.useWorkerPicker({
    setValue: (newValue) => form.setFieldValue('owners', newValue),
    value: owners ?? [],
    mode: 'multiple',
  });
  const { getSelectProps: getExceptionUsersSelectProps } = Picker.useWorkerPicker();
  const navigate = useNavigate();

  const onFinish = (formData: AccessConflictFormData) =>
    create.mutate({
      accessSource: formData.access?.accessSource,
      departmentId: formData.department,
      owners: formData.owners.map((owner) => owner.value as string),
      employeeSubtype: formData.employeeType === EmployeeType.CONTINGENT_WORKER ? formData.employeeSubtype : undefined,
      employeeType: formData.employeeType as EmployeeType,
      exceptions: formData.exceptions.flatMap((exception) => ({
        wwid: exception.user.value as string,
        justification: exception.justification,
      })),
      externalId: formData.access?.externalId,
    });

  if (ruleType !== 'access-conflict' && ruleType !== 'department-conflict') {
    return <NotFoundPage />;
  }

  if (isFetching || !data || copyQuery.isFetching) {
    return <Spin />;
  }

  const initialValues: AccessConflictFormData = copyQuery.data
    ? {
        access: copyQuery.data.conflictAccess,
        department: copyQuery.data.department?.externalId,
        employeeType: copyQuery.data.employeeType,
        employeeSubtype: copyQuery.data.employeeSubtype ?? undefined,
        owners: copyQuery.data.owners.map((owner) => ({
          label: owner.user.name,
          value: owner.user.enterpriseId,
        })),
        exceptions: copyQuery.data.exceptions.map((exception) => ({
          user: {
            label: exception.user.name,
            value: exception.user.enterpriseId,
          },
          justification: exception.justification,
        })),
      }
    : {
        employeeType: data?.employeeType !== EmployeeType.ANY ? data.employeeType : undefined,
        owners: user ? [{ label: user.name, value: user.enterpriseId }] : [],
        exceptions: [],
      };

  const accessDescription: DescriptionsItemType[] = [
    {
      key: 'name',
      label: 'Name',
      children: data.name,
    },
    {
      key: 'source',
      label: 'Access Source',
      children: data.accessSource,
    },
    {
      key: 'employeeType',
      label: 'Employee Type',
      children: data.employeeType,
    },
  ];

  if (create.isSuccess) {
    return (
      <Result
        status="success"
        title="Successfully created an access rule!"
        subTitle={`Created an access conflict for ${data.name}`}
        extra={[
          <Button type="primary" key="go to access" onClick={() => navigate('../')}>
            Go To Manage Rules
          </Button>,
          <Button
            key="create another"
            onClick={() => {
              create.reset();
              form.resetFields();
            }}
          >
            Create Another
          </Button>,
        ]}
      />
    );
  }

  if (create.isError) {
    return (
      <Result
        status="error"
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        title={(create.error.response?.data as any)?.message ?? create.error.message}
        extra={[
          <Button type="primary" key="go to access" onClick={() => navigate('../')}>
            Go To Manage Rules
          </Button>,
          <Button
            key="create another"
            onClick={() => {
              create.reset();
            }}
          >
            Return to Create Page
          </Button>,
        ]}
      />
    );
  }

  return (
    <>
      <Breadcrumb
        style={{ margin: '16px 0' }}
        items={[{ title: <Link to="/">Home</Link> }, { title: <Link to="../">Access</Link> }, { title: PAGE_TITLE }]}
      />
      <Layout.Content {...formLayoutProps}>
        <PageLayout.Title title={PAGE_TITLE} />
        <Form
          form={form}
          initialValues={initialValues}
          layout="vertical"
          style={{ padding: '1em' }}
          onFinish={onFinish}
        >
          <Descriptions title="Access Details" items={accessDescription} />
          <Form.Item
            label="Employee Type"
            name="employeeType"
            rules={[{ required: true, message: 'Employee type is required.' }]}
          >
            <Radio.Group buttonStyle="solid" disabled={data.employeeType !== EmployeeType.ANY}>
              <Radio.Button value={EmployeeType.INTEL}>Intel Employee</Radio.Button>
              <Radio.Button value={EmployeeType.CONTINGENT_WORKER}>Contingent Worker</Radio.Button>
            </Radio.Group>
          </Form.Item>
          {employeeType === EmployeeType.CONTINGENT_WORKER && (
            <Form.Item
              label="Employee Subtype"
              name="employeeSubtype"
              rules={[
                { required: employeeType === EmployeeType.CONTINGENT_WORKER, message: 'Employee subtype is required.' },
              ]}
            >
              <Radio.Group buttonStyle="solid">
                <Radio.Button value={EmployeeSubtype.ODC}>ODC</Radio.Button>
                <Radio.Button value={EmployeeSubtype.OFFSITE}>OFFSITE</Radio.Button>
                <Radio.Button value={EmployeeSubtype.VPN}>VPN</Radio.Button>
                <Radio.Button value={EmployeeSubtype.EDA}>EDA</Radio.Button>
              </Radio.Group>
            </Form.Item>
          )}
          {ruleType === 'access-conflict' ? (
            <Form.Item
              label="Access Conflict"
              name="access"
              dependencies={['employeeType']}
              rules={[
                { required: true, message: 'Access Conflict is required.' },
                ({ getFieldValue }) => ({
                  validator: accessesMatchesEmployeeType(getFieldValue('employeeType')),
                }),
              ]}
            >
              <AccessSelect
                disabled={{ select: (x) => x.accessSource === data.accessSource && x.externalId === data.externalId }}
                mode="single"
              />
            </Form.Item>
          ) : (
            <Form.Item
              label="Department Conflicts"
              name="department"
              rules={[{ required: true, message: 'Department is required.' }]}
            >
              <DepartmentCascader />
            </Form.Item>
          )}
          <Form.Item label="Owners" name="owners" rules={[{ required: true }]}>
            <Select {...getSelectProps({ placeholder: 'Search Intel workers to set as owners' })} />
          </Form.Item>
          <Form.Item
            label="Exceptions"
            name="exceptions"
            rules={[
              {
                validator: exceptionUniquenessValidator,
              },
            ]}
          >
            <Tables.DataTable recordType="Exception">
              <Form.Item label="User" name="user" rules={[{ required: true }]}>
                <Select {...getExceptionUsersSelectProps({ style: { minWidth: '275px' } })} />
              </Form.Item>
              <Form.Item label="Justification" name="justification" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Tables.DataTable>
          </Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={create.isPending}>
              Save
            </Button>
            <Button onClick={() => form.resetFields()}>Reset</Button>
            <Button onClick={() => navigate('../')}>Cancel</Button>
          </Space>
        </Form>
      </Layout.Content>
    </>
  );
};
